import debug from 'debug'
import { OnerwayPaymentMethod } from 'pixai-contract/payment/shared'
import { type FC, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useAsync } from 'react-use'
import { match } from 'ts-pattern'
import MdiLoading from '~icons/mdi/loading'
import MarkdownRenderer from 'src/components/ui/MarkdownRenderer'
import type {
  OpenPaymentDialogReturns,
  PaymentExecuteFn,
} from 'src/services/helpers/payment'
import { PaymentProvider } from 'src/services/helpers/payment'
import { getApiEnv } from 'src/utils/config'
import { genRandomString } from 'src/utils/genId'
import { track } from 'src/utils/tracker'
import Pacypay from '../../../../vendor/onerway/onerway'
import { buildOnerwayError, isOnerwayPaymentError } from './onerwayErrorUtils'

const log = debug('pixai:payment:onerway')

function ensureOnerwayLocale(code: string): string | undefined {
  const SUPPORTED_LOCALES = [
    'en',
    'ar',
    'de',
    'es',
    'fi',
    'fr',
    'it',
    'ja',
    'ko',
    'nl',
    'no',
    'pl',
    'pt',
    'ru',
    'sv',
    'th',
    'zh-TW',
  ]
  if (SUPPORTED_LOCALES.includes(code)) {
    return code
  }
  // attempt to map the language code to the supported locales
  const lang = code.split('-')[0]
  if (SUPPORTED_LOCALES.includes(lang)) {
    return lang
  }
  return undefined
}

interface PaymentDialogContentOnerwayGoogleProps {
  execute: PaymentExecuteFn
  trackingFnAfterFetch?: () => void
  onSucceeded?: (_: OpenPaymentDialogReturns) => void
  onFailed?: (error: Error) => void
}

export const PaymentDialogContentOnerwayGoogle: FC<
  PaymentDialogContentOnerwayGoogleProps
> = ({ execute, trackingFnAfterFetch, onSucceeded, onFailed }) => {
  const [paymentConfig, setPaymentConfig] = useState<{
    transactionId: string
    orderId: string
    subProductType?: string
  } | null>(null)
  const { t, i18n } = useTranslation(['membership'])
  const containerId = useRef(
    'onerway_google_pay_' + genRandomString(16),
  ).current
  const [threeDSecureUrl, setThreeDSecureUrl] = useState<string | null>(null)

  const { error } = useAsync(async () => {
    const result = await execute({
      provider: PaymentProvider.onerway,
      providerOptions: { paymentMethod: OnerwayPaymentMethod.GOOGLE_PAY },
    })
    setPaymentConfig({
      transactionId: result.transactionId,
      orderId: result.orderId,
      subProductType: result.subProductType,
    })
    trackingFnAfterFetch?.()
  })

  useEffect(() => {
    if (error) {
      log('[Payment:Onerway] Failed to fetch transaction ID', error)
      onFailed?.(error)
    }
  }, [error])

  useLayoutEffect(() => {
    if (!paymentConfig) return

    // We must wait until the dialog is mounted before initializing the Onerway transaction
    const instance = new Pacypay(paymentConfig.transactionId, {
      container: containerId,
      locale: ensureOnerwayLocale(i18n.language) ?? 'en',
      environment:
        getApiEnv().isProd || getApiEnv().isStaging ? 'production' : 'sandbox',
      mode: 'GooglePay',
      config: {
        googlePayButtonType: 'pay',
        googlePayButtonColor: 'black', // Button color: 'black' or 'white'
        googlePayEnvironment:
          getApiEnv().isProd || getApiEnv().isStaging ? 'PRODUCTION' : 'TEST',
        buttonWidth: '100%', // Button width
        buttonHeight: '100%', // Button height
        buttonRadius: '8px', // Button border radius
      },
      onPaymentCompleted: res => {
        log('[Payment:Onerway] Payment completed with response', res)
        const txtInfo = res.data
        const respCode = res.respCode
        if (respCode === '20000') {
          track('onerway_payment', {
            provider: 'google_pay',
            status: txtInfo.status,
          })
          match(txtInfo.status)
            // requires onerway 3ds verification
            .with('R', () => {
              if (txtInfo.redirectUrl) {
                window.location.href = txtInfo.redirectUrl
                setThreeDSecureUrl(txtInfo.redirectUrl)
                // destroy the instance, remove all children
                const container = document.getElementById(containerId)
                if (container) {
                  while (container?.firstChild) {
                    container.removeChild(container.firstChild)
                  }
                  container.style.setProperty('height', 'auto')
                  container.style.setProperty('width', 'auto')
                }
              } else {
                toast.error('Redirect URL not found')
              }
            })
            // success, but maybe trigger googlepay 3ds
            .with('S', () => {
              onSucceeded?.({
                finalizedPaymentProvider: PaymentProvider.onerway,
                orderId: paymentConfig.orderId,
              })
            })
            .with('F', () => {
              toast.error(t('membership:payment.failed-generic'))
              onFailed?.(new Error(t('membership:payment.failed-generic')))
            })
            .exhaustive()
        } else {
          console.error('[Payment:Onerway] Payment error', res)
          toast.error(t('error:codes.payment-failed'))
          onFailed?.(
            buildOnerwayError({
              respCode: res?.respCode,
              respMsg: res?.respMsg,
              data: res?.data,
            }),
          )
        }
      },
      onError: err => {
        if (err.respCode === '1005') {
          // cancel and do nothing
          return
        }
        console.error('[Payment:Onerway] Payment error', err, paymentConfig)
        toast.error(t('membership:payment.failed-generic'))
        if (isOnerwayPaymentError(err)) {
          onFailed?.(
            buildOnerwayError({
              respCode: err.respCode,
              respMsg: err.respMsg,
              data: (err as any)?.data,
            }),
          )
          return
        }
        onFailed?.(
          new Error('Unknown Error occurred during payment processing.'),
        )
      },
      onChangeWH: o => {
        const { width, height } = JSON.parse(o)
        document
          .getElementById(containerId)
          ?.style.setProperty('height', `${height + 20}px`)
        document
          .getElementById(containerId)
          ?.style.setProperty('width', `${width}px`)
      },
    })

    // dirty hack to remove `title` from the `iframe` pacypay creates.
    // That `title` is both annoying and makes the iframe 100000% less trustworthy
    const timer = setInterval(() => {
      const iframe = document
        .getElementById(containerId)
        ?.querySelector('iframe')
      if (iframe) {
        iframe.title = ''
        clearInterval(timer)
      }
    }, 1000)

    log('[Payment:Onerway] Initialized Onerway instance', instance)

    return () => {
      clearInterval(timer)
    }
  }, [paymentConfig])

  if (threeDSecureUrl) {
    return (
      <div className="w-full rounded-lg flex flex-col items-center duration-300 justify-center text-black/75 text-center bg-white select-none px-4 py-8 gap-4">
        <div className="w-full flex flex-col items-center justify-center gap-2 py-2">
          <MdiLoading className="size-6 animate-spin" />

          <div className="opacity-70 text-sm mt-2">
            <Trans
              i18nKey="membership:payment.threedsecure.title"
              components={[
                <a
                  href={threeDSecureUrl}
                  rel="noreferrer noopener"
                  className="text-blue-500 hover:underline"
                />,
              ]}
            />
          </div>
        </div>

        <div className="h-px w-full bg-black/10" />

        <div className="opacity-70 text-left w-full [&>p:not(:last-child)]:mb-2 text-xs">
          <MarkdownRenderer>
            {t('membership:payment.threedsecure.faq')}
          </MarkdownRenderer>
        </div>
      </div>
    )
  }

  return paymentConfig ? (
    <div
      className="h-[42px] w-full bg-neutral-100 rounded-lg overflow-y-auto flex items-center justify-center duration-300"
      id={containerId}
    />
  ) : (
    <div className="h-[100px] w-full rounded-lg flex items-center duration-300 justify-center text-black/75 text-center text-sm bg-neutral-100 select-none">
      <MdiLoading className="size-6 animate-spin" />
    </div>
  )
}
